import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { removeLoadingKey } from '../helpers/common';
import { toast } from 'react-toastify';
import axios from 'axios';

export const ReportsLoadingKeys = {
  getReportData: "GET_REPORT_DATA",
  updateLocationsData: "UPDATE_LOCATIONS_DATA",
  getLocationsOfReports: "GET_LOCATIONS_OF_REPORTS",
  getPinnedReports: "GET_PINNED_REPORTS",
  togglePinnedReport: "TOGGLE_PINNED_REPORT",
  getAvailableReports: "GET_AVAILABLE_REPORTS",
  archiveReport: "ARCHIVE_REPORT",
  unarchiveReport: "UNARCHIVE_REPORT",
  archiveReports: "ARCHIVE_REPORTS",
  unarchiveReports: "UNARCHIVE_REPORTS",
  setPinnedReports: "SET_PINNED_REPORTS",
}

// Async thunk for fetching report data
export const getReportData = createAsyncThunk(
    'reports/getReportData',
    async ({ reportType, startDate, endDate, page, pageSize, sortField, sortDirection }) => {
        const params = new URLSearchParams({
            startDate,
            endDate,
            page: page || 1,
            pageSize: pageSize || 50
        });

        if (sortField) {
            params.append('sortField', sortField);
            params.append('sortDirection', sortDirection);
        }

        const response = await fetch(`/api/v1/reports/${reportType}?${params}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch report data');
        }

        return response.json();
    }
);

export const getAvailableReports = createAsyncThunk(
  'reports/getAvailableReports',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/reports');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load reports');
    }
  },
);

const archiveReportRequest = (path, type) => createAsyncThunk(
  `reports/${type}`,
  async ({ reportId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(path, { reportId });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || `Failed to ${type} report`);
    }
  },
);

export const archiveReport = archiveReportRequest('/api/v1/reports/archive', 'archiveReport');
export const unarchiveReport = archiveReportRequest('/api/v1/reports/unarchive', 'unarchiveReport');

const archiveReportsRequest = (path, type) => createAsyncThunk(
  `reports/${type}`,
  async ({ reportIds }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(path, { reportIds });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || `Failed to ${type} reports`);
    }
  },
);

export const archiveReports = archiveReportsRequest('/api/v1/reports/archive-bulk', 'archiveReports');
export const unarchiveReports = archiveReportsRequest('/api/v1/reports/unarchive-bulk', 'unarchiveReports');

export const getLocationsOfReports = createAsyncThunk(
  'reports/getLocationsOfReports',
  async ({}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/get-locations-of-reports`);
      return data;
    } catch (error) {
        if ('string' === typeof error.response.data) {
          return rejectWithValue({ error: error.response.data })
        } else {
          return rejectWithValue({ error: error.response.data.errors })
        }
    }
  }
);

export const updateLocationsOfReports = createAsyncThunk(
  'reports/updateLocationsOfReports',
  async ({
    locations,
    silent = false,
  }, { rejectWithValue }) => {
    try {
        const { data } = await axios.post(`/api/v1/update-locations-of-reports`, { locations });
        return { ...data, silent };
    } catch (error) {
        if ('string' === typeof error.response.data) {
          return rejectWithValue({ error: error.response.data })
        } else {
          return rejectWithValue({ error: error.response.data.errors })
        }
    }
  }
);

export const getPinnedReports = createAsyncThunk(
  'reports/getPinnedReports',
  async ({}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/pinned-reports`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response?.data) {
        return rejectWithValue({ error: error.response.data });
      }
      return rejectWithValue({ error: error.response?.data?.errors });
    }
  },
);

export const togglePinnedReport = createAsyncThunk(
  'reports/togglePinnedReport',
  async ({ reportId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/toggle-pinned-report`, {
        report_id: reportId,
      });
      return { reportId, pinned: Boolean(data?.pinned) };
    } catch (error) {
      if ('string' === typeof error.response?.data) {
        return rejectWithValue({ error: error.response.data });
      }
      return rejectWithValue({ error: error.response?.data?.errors });
    }
  },
);

export const setPinnedReports = createAsyncThunk(
  'reports/setPinnedReports',
  async ({ reportIds, pinned }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/set-pinned-reports', {
        report_ids: reportIds,
        pinned,
      });
      return data;
    } catch (error) {
      if (typeof error.response?.data === 'string') {
        return rejectWithValue({ error: error.response.data });
      }
      return rejectWithValue({ error: error.response?.data?.errors || error.response?.data?.message });
    }
  },
);

export const recordReportLastViewed = createAsyncThunk(
  'reports/recordReportLastViewed',
  async ({ reportName }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/record-report-last-viewed`, {
        report_name: reportName,
      });
      return { reportName, lastViewed: data?.last_viewed };
    } catch (error) {
      if ('string' === typeof error.response?.data) {
        return rejectWithValue({ error: error.response.data });
      }
      return rejectWithValue({ error: error.response?.data?.errors });
    }
  },
);

const reportsSlice = createSlice({
    name: 'reports',
    initialState: {
        data: [],
        locationsOfReports: [],
        pinnedReportIds: [],
        archivedReports: {},
        loadingKeys: [],
        loading: false,
        error: null,
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1
    },
    reducers: {
        clearReportData: (state) => {
            state.data = [];
            state.totalRecords = 0;
            state.totalPages = 0;
            state.currentPage = 1;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getReportData.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.deleteProduct];
            })
            .addCase(getAvailableReports.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.getAvailableReports];
            })
            .addCase(getAvailableReports.fulfilled, (state, action) => {
                const reports = action.payload?.reports || [];
                const archivedReportIds = action.payload?.archivedReportIds || [];
                state.archivedReports = archivedReportIds.reduce((acc, reportId) => {
                    acc[reportId] = { reportId, archived: true };
                    return acc;
                }, {});
                reports.forEach((report) => {
                    if (report?.archived && report?.reportId) {
                        state.archivedReports[report.reportId] = report;
                    }
                });
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.getAvailableReports, state.loadingKeys);
            })
            .addCase(getAvailableReports.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.getAvailableReports, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(archiveReport.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.archiveReport];
            })
            .addCase(archiveReport.fulfilled, (state, action) => {
                const report = action.payload;
                if (report?.reportId && report.archived) {
                    state.archivedReports[report.reportId] = report;
                }
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.archiveReport, state.loadingKeys);
            })
            .addCase(archiveReport.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.archiveReport, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(unarchiveReport.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.unarchiveReport];
            })
            .addCase(unarchiveReport.fulfilled, (state, action) => {
                const reportId = action.payload?.reportId;
                if (reportId) delete state.archivedReports[reportId];
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.unarchiveReport, state.loadingKeys);
            })
            .addCase(unarchiveReport.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.unarchiveReport, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(archiveReports.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.archiveReports];
            })
            .addCase(archiveReports.fulfilled, (state, action) => {
                (action.payload?.reportIds || []).forEach((reportId) => {
                    state.archivedReports[reportId] = { reportId, archived: true };
                });
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.archiveReports, state.loadingKeys);
            })
            .addCase(archiveReports.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.archiveReports, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(unarchiveReports.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.unarchiveReports];
            })
            .addCase(unarchiveReports.fulfilled, (state, action) => {
                (action.payload?.reportIds || []).forEach((reportId) => {
                    delete state.archivedReports[reportId];
                });
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.unarchiveReports, state.loadingKeys);
            })
            .addCase(unarchiveReports.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.unarchiveReports, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(setPinnedReports.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.setPinnedReports];
            })
            .addCase(setPinnedReports.fulfilled, (state, action) => {
                const reportIds = action.payload?.reportIds || [];
                const pinned = Boolean(action.payload?.pinned);
                if (pinned) {
                    state.pinnedReportIds = Array.from(new Set([...state.pinnedReportIds, ...reportIds]));
                } else {
                    const selected = new Set(reportIds);
                    state.pinnedReportIds = state.pinnedReportIds.filter((reportId) => !selected.has(reportId));
                }
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.setPinnedReports, state.loadingKeys);
            })
            .addCase(setPinnedReports.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.setPinnedReports, state.loadingKeys);
                state.error = action.payload || action.error?.message;
            })
            .addCase(getReportData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload.data;
                state.totalRecords = action.payload.totalRecords;
                state.totalPages = action.payload.totalPages;
                state.currentPage = action.payload.page;
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.deleteProduct, state.loadingKeys);
            })
            .addCase(getReportData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.deleteProduct, state.loadingKeys);
            })
            .addCase(getLocationsOfReports.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.getLocationsOfReports];
            })
            .addCase(getLocationsOfReports.fulfilled, (state, action) => {
                // Accept both array payloads and { items } shaped payloads from the API
                state.locationsOfReports = Array.isArray(action.payload)
                    ? action.payload
                    : (action.payload?.items || []);
                state.loading = false;
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.getLocationsOfReports, state.loadingKeys);
            })
            .addCase(getLocationsOfReports.rejected, (state, action) => {
                state.error = action.error.message;
                state.locationsOfReports = [];
                state.loading = false;
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.getLocationsOfReports, state.loadingKeys);
            })
            .addCase(getPinnedReports.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.getPinnedReports];
            })
            .addCase(getPinnedReports.fulfilled, (state, action) => {
                const items = Array.isArray(action.payload)
                    ? action.payload
                    : (action.payload?.items || []);
                state.pinnedReportIds = items
                    .map((item) => item?.report_id)
                    .filter(Boolean);
                state.loading = false;
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.getPinnedReports, state.loadingKeys);
            })
            .addCase(getPinnedReports.rejected, (state, action) => {
                state.error = action.error?.message;
                state.pinnedReportIds = [];
                state.loading = false;
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.getPinnedReports, state.loadingKeys);
            })
            .addCase(togglePinnedReport.pending, (state) => {
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.togglePinnedReport];
            })
            .addCase(togglePinnedReport.fulfilled, (state, action) => {
                const { reportId, pinned } = action.payload || {};
                if (!reportId) return;
                if (pinned) {
                    if (!state.pinnedReportIds.includes(reportId)) {
                        state.pinnedReportIds.push(reportId);
                    }
                } else {
                    state.pinnedReportIds = state.pinnedReportIds.filter((id) => id !== reportId);
                }
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.togglePinnedReport, state.loadingKeys);
            })
            .addCase(togglePinnedReport.rejected, (state, action) => {
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.togglePinnedReport, state.loadingKeys);
                console.error('Failed to toggle pinned report.', action.error?.message);
            })
            .addCase(updateLocationsOfReports.pending, (state) => {
                state.loading = true;
                state.loadingKeys = [...state.loadingKeys, ReportsLoadingKeys.updateLocationsData];
            })
            .addCase(updateLocationsOfReports.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.updateLocationsData, state.loadingKeys);
                if (!action.payload?.silent) {
                    toast.success(
                        `The locations is updated successfully.`, { position: "bottom-right" }
                    );
                }
            })
            .addCase(updateLocationsOfReports.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message;
                state.loadingKeys = removeLoadingKey(ReportsLoadingKeys.updateLocationsData, state.loadingKeys);
                if (!action.meta?.arg?.silent) {
                    toast.error(
                        `The locations can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
                    );
                }
            })
            .addCase(recordReportLastViewed.fulfilled, (state, action) => {
                const { reportName, lastViewed } = action.payload || {};
                if (!reportName) return;
                const viewedAt = lastViewed || new Date().toISOString();
                const index = state.locationsOfReports.findIndex(
                    (item) => item?.report_name === reportName,
                );
                if (index >= 0) {
                    state.locationsOfReports[index] = {
                        ...state.locationsOfReports[index],
                        last_viewed: viewedAt,
                        lastViewed: viewedAt,
                    };
                } else {
                    state.locationsOfReports.push({
                        report_name: reportName,
                        last_viewed: viewedAt,
                        lastViewed: viewedAt,
                        tab_name: null,
                    });
                }
            })
    }
});

export const { clearReportData } = reportsSlice.actions;

export default reportsSlice.reducer; 